// Typed wrappers for the per-merchant payout routing rules surface.
// Backend Cut 2 — POST/GET/PATCH/DELETE /v1/merchants/:id/routing-rules.
//
// Cut 2 picks the FIRST candidate of the first matching rule and overrides
// the operator-supplied rail with it. Per-candidate auto_approve flags
// override the rail-level flag. Cut 3 will walk the full candidate list
// with balance checks; the type already carries the array so the panel
// can grow the multi-candidate UI without an API shape change.
import { apiClient } from "./client";

/**
 * RoutingRuleMatch — predicate clauses, all AND-combined. Every field is
 * optional. Omitted fields are "don't care". An entirely empty match is a
 * catch-all (typically used at the highest priority value as the
 * fallthrough rule).
 *
 * `mode` mirrors payouts.txn_mode (1=IMPS, 2=NEFT, 3=UPI). Amount bounds
 * are INCLUSIVE in paise. `currency` is uppercase ISO-4217 (today always
 * "INR"; the field is here for forward compat).
 */
export interface RoutingRuleMatch {
  mode?: 1 | 2 | 3;
  amount_minor_gte?: number;
  amount_minor_lte?: number;
  currency?: string;
}

/**
 * RoutingRuleCandidate — one rail to try when the rule fires. Cut 2 uses
 * the first; Cut 3 will iterate with balance checks. `auto_approve` here
 * is per-candidate and overrides the rail-level flag.
 */
export interface RoutingRuleCandidate {
  bank_account_id: number;
  auto_approve: boolean;
}

export interface RoutingRule {
  id: number;
  merchant_id: number;
  priority: number;
  match: RoutingRuleMatch;
  candidates: RoutingRuleCandidate[];
  created_at: string;
  updated_at: string;
}

export interface CreateRoutingRulePayload {
  priority?: number;
  match: RoutingRuleMatch;
  candidates: RoutingRuleCandidate[];
}

export interface UpdateRoutingRulePayload {
  priority?: number;
  match?: RoutingRuleMatch;
  candidates?: RoutingRuleCandidate[];
}

/**
 * listRoutingRules — GET /v1/merchants/:merchantId/routing-rules
 * Admin-only. Returned in evaluation order (priority ASC, then id ASC).
 */
export async function listRoutingRules(
  merchantId: number,
): Promise<RoutingRule[]> {
  const resp = await apiClient.get<{ routing_rules: RoutingRule[] }>(
    `/v1/merchants/${merchantId}/routing-rules`,
  );
  return resp.data.routing_rules;
}

export async function createRoutingRule(
  merchantId: number,
  payload: CreateRoutingRulePayload,
): Promise<RoutingRule> {
  const resp = await apiClient.post<RoutingRule>(
    `/v1/merchants/${merchantId}/routing-rules`,
    payload,
  );
  return resp.data;
}

export async function updateRoutingRule(
  merchantId: number,
  ruleId: number,
  payload: UpdateRoutingRulePayload,
): Promise<RoutingRule> {
  const resp = await apiClient.patch<RoutingRule>(
    `/v1/merchants/${merchantId}/routing-rules/${ruleId}`,
    payload,
  );
  return resp.data;
}

export async function deleteRoutingRule(
  merchantId: number,
  ruleId: number,
): Promise<void> {
  await apiClient.delete(
    `/v1/merchants/${merchantId}/routing-rules/${ruleId}`,
  );
}
